import React from "react"
import {
    Box,
    componentStyles,
    HeadingLarge,
    useTheme,
} from "@mallardbay/lib-react-components"

import SEO from "~components/shared/seo"
import ResponsiveBlock from "~components/shared/responsive-block"
import BlogEntrySocials from "~components/pages/blog/blog-entry-socials"
import COPY_CONSTANTS from "~config/copy-constants"
import type { BlogEntryAuthorType, BlogEntryType } from "~utils/blog-helpers"
import { isBlogEntryForOutfitter } from "~utils/blog-helpers"
import { BUILDER_IO_CSS_CLASS } from "~config/constants"
import AspectRatioImage from "~components/shared/todo-lib-react-components/aspect-ratio-image"
import BlogEntryAuthor from "~components/pages/blog/blog-entry-author"
import BackButton from "~components/shared/back-button"
import { navigateToBlog } from "~utils/navigation-helpers"
import BlogPreviewList from "~components/pages/blog/blog-entry-preview-list"
import { TEST_IDS } from "~config/test-ids"

type Props = {
    readonly pageContext: {
        blogEntry: BlogEntryType
        blogEntryAuthor: BlogEntryAuthorType
        otherEntries: BlogEntryType[] | null
    }
}

export default function BlogEntry({
    pageContext: { blogEntry, blogEntryAuthor, otherEntries },
}: Props) {
    const blogEntryData = blogEntry?.data

    const styles = useStyles()
    if (!blogEntryData) return null

    const onBackButtonClick = () => {
        const isForOutfitter = isBlogEntryForOutfitter(blogEntry)
        void navigateToBlog(isForOutfitter)
    }

    return (
        <ResponsiveBlock>
            <SEO
                title={blogEntryData.seoTitle}
                description={blogEntryData.shortDescription}
                image={blogEntryData.coverImage}
            />

            <Box style={styles.root} data-testid={TEST_IDS.BLOG_ENTRY}>
                <Box style={styles.columnLeft}>
                    <BackButton
                        label={COPY_CONSTANTS.BACK_TO_BLOG}
                        onBackClick={onBackButtonClick}
                    />
                    <HeadingLarge>{blogEntryData.title}</HeadingLarge>
                    <BlogEntryAuthor
                        blogEntry={blogEntry}
                        blogEntryAuthor={blogEntryAuthor}
                    />
                    <BlogEntrySocials imageUrl={blogEntryData.coverImage} />
                    <br />
                    {blogEntryData.coverImage && (
                        <AspectRatioImage
                            src={blogEntryData.coverImage}
                            imageStyle={styles.headerImage}
                            ratio={5 / 3}
                        />
                    )}
                    <Box style={styles.contentContainer}>
                        <div
                            // This class is important because this is how we set styles in theme provider
                            className={BUILDER_IO_CSS_CLASS}
                            dangerouslySetInnerHTML={{
                                __html: blogEntryData.content ?? "",
                            }}
                        />
                    </Box>
                </Box>

                {/* Blog sidebar */}
                <Box style={styles.columnRight}>
                    <Box style={styles.blogSidebarContainer}>
                        <BlogPreviewList
                            blogEntries={otherEntries}
                            shouldShowBorder={true}
                        />
                    </Box>
                </Box>
            </Box>
        </ResponsiveBlock>
    )
}

function useStyles() {
    const theme = useTheme()
    const sharedSpacing = theme.space[5]
    const sharedImageBorderRadious = theme.radii.lg

    return componentStyles({
        root: {
            marginTop: sharedSpacing,
            marginBottom: sharedSpacing,
            display: "flex",
            flexDirection: {
                base: "column",
                lg: "row",
            },
        },
        columnLeft: {
            width: {
                base: "100%",
                lg: "70%",
            },
            paddingRight: {
                lg: sharedSpacing,
            },
            marginRight: {
                lg: sharedSpacing,
            },
        },
        columnRight: {
            bottomMargin: "100px",
            width: {
                base: "100%",
                lg: "30%",
            },
            minWidth: {
                lg: "350px",
            },
        },
        blogSidebarContainer: {
            position: "sticky",
            top: "100px",
        },
        contentContainer: {
            marginTop: theme.space[5],
        },
        headerImage: {
            width: "100%",
            height: "100%",
            fit: "cover",
            borderRadius: sharedImageBorderRadious,
            transition: "scale 0.3s ease-in-out",
        },
    })
}
